import React, { useState } from 'react';
import { Outlet, useOutletContext, useLocation, useNavigate, Link, Navigate } from 'react-router';
import { Button, Typography, Divider, Grid } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useAuth } from '../providers/AuthProvider';
import { AuthWithSocial } from '../pages/Authentication/AuthWithSocial';
import { socialLoginButtonStyles } from '../pages/Authentication/authStyles';
import { AuthCarousel } from './AuthCarousel';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

// --- Context ---

export interface AuthLayoutContext {
  isMobile: boolean;
  setMobileView: (view: 'landing' | 'form') => void;
}

export function useAuthLayoutContext() {
  return useOutletContext<AuthLayoutContext>();
}

// --- Desktop styles ---

const desktopLayoutStyle: React.CSSProperties = {
  display: 'flex',
  height: '100dvh',
  overflow: 'hidden',
};

const leftPanelStyle: React.CSSProperties = {
  flex: '0 0 420px',
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
  padding: '48px',
  overflowY: 'auto',
};

const leftPanelContentStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: 400,
  width: '100%',
  margin: '0 auto',
};

const logoStyle: React.CSSProperties = {
  height: 40,
  marginBottom: 24,
};

const rightPanelStyle: React.CSSProperties = {
  flex: '1 1 0%',
  minWidth: 0,
  padding: 12,
};

// --- Mobile styles ---

const mobileLayoutStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100dvh',
  overflow: 'hidden',
};

const mobileCarouselWrapperStyle: React.CSSProperties = {
  padding: '12px 12px 0',
};

const mobileActionsStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  gap: 12,
};

const mobileFormStyle: React.CSSProperties = {
  height: '100dvh',
  overflowY: 'auto',
  padding: '24px',
  background: '#fff',
};

const mobileFormContentStyle: React.CSSProperties = {
  maxWidth: 400,
  margin: '0 auto',
};

// --- Main layout ---

export const AuthLayout: React.FC = () => {
  const { loggedIn, setAuthUser } = useAuth();
  const screens = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = !screens.md;
  const [mobileView, setMobileView] = useState<'landing' | 'form'>('landing');

  if (loggedIn) {
    return <Navigate to="/" replace />;
  }

  const context = { isMobile, setMobileView } satisfies AuthLayoutContext;
  const isLoginRoute = location.pathname === '/login';

  // --- Mobile: form view ---
  if (isMobile && mobileView === 'form') {
    return (
      <div style={mobileFormStyle}>
        <div style={mobileFormContentStyle}>
          <Outlet context={context} />
        </div>
      </div>
    );
  }

  // --- Mobile: landing view ---
  if (isMobile) {
    return (
      <div style={mobileLayoutStyle}>
        <div style={mobileCarouselWrapperStyle}>
          <AuthCarousel height="calc(60vh - 12px)" compact />
        </div>
        <div style={mobileActionsStyle}>
          <Title level={4} style={{ margin: 0 }}>
            Build your dream home
          </Title>
          <Text type="secondary">Sign in to start your ADU project</Text>
          <AuthWithSocial
            onComplete={setAuthUser}
            type="login"
            successMessage="Login successful!"
            errorMessage="Login failed"
          />
          <Button
            className={socialLoginButtonStyles}
            icon={<MailOutlined />}
            onClick={() => {
              if (!isLoginRoute) navigate('/login');
              setMobileView('form');
            }}>
            Sign in with Email
          </Button>
          <Divider style={{ margin: '8px 0' }} />
          <Text>
            {isLoginRoute ? (
              <>
                Don&apos;t have an account?{' '}
                <Link to="/register" onClick={() => setMobileView('form')}>
                  Register
                </Link>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <Link to="/login" onClick={() => setMobileView('landing')}>
                  Log in
                </Link>
              </>
            )}
          </Text>
        </div>
      </div>
    );
  }

  // --- Desktop ---
  return (
    <div style={desktopLayoutStyle}>
      <div style={leftPanelStyle}>
        <div>
          <img src="/public/images/logo_circle.png" alt="HomeWiP" style={logoStyle} />
        </div>
        <div style={leftPanelContentStyle}>
          <Outlet context={context} />
        </div>
      </div>
      <div style={rightPanelStyle}>
        <AuthCarousel height="calc(100dvh - 24px)" />
      </div>
    </div>
  );
};
